import { createBrowserRouter } from "react-router-dom";
import { utils } from "@ucl/library";
import PagesLayout from "@pages/PagesLayout";
import { HomePageWrapper } from "@pages/HomePage";
import { BankerApplicationViewPage } from "@pages/BankerApplicationViewPage";
import { MultiAuth0Provider } from "@/providers/MultiAuth0Provider";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import { LoginCallback } from "@/pages/Auth/LoginCallback";
import { LogoutSuccess } from "@/pages/Auth/LogoutSuccess";
import UserContextProvider from "@/contexts/UserContext/UserContext";
import SessionContextProvider from "@/contexts/SessionContext/SessionContext";
import { BankerAdminPage } from "@/pages/BankerAdminPage";
import BankerApplicationsPage from "@/pages/BankerApplicationsPage";
import { ConsumerPortalPage } from "@/pages/ConsumerPortalPage";
import { ConsumerPortalTasksPage } from "@/pages/ConsumerPortalTasksPage";
import { ConsumerPortalApplicationsPage } from "@/pages/ConsumerPortalApplicationsPage";
import { ConsumerPortalApplicationViewPage } from "@/pages/ConsumerPortalApplicationViewPage";
import { ConsumerInvitePage } from "@/pages/ConsumerInvitePage";
import { ApplicationSubmittedPage } from "@/pages/ApplicationSubmittedPage";
import { CreditAuthorizationPage } from "@/pages/CreditAuthorizationPage";
import { faDashboard, faGear } from "@fortawesome/free-solid-svg-icons";
import { NotFound } from "@/pages/NotFound";

// Consumer Routes (Public financial application)
const consumerRoutes: utils.routes.UclRouteObject[] = [
  {
    path: "/",
    label: "Consumer Application",
    isAvailable: true,
    showInNavigation: false,
    element: (
      <MultiAuth0Provider clientType="consumer">
        <UserContextProvider>
          <SessionContextProvider>
            <PagesLayout redirectPath="/" />
          </SessionContextProvider>
        </UserContextProvider>
      </MultiAuth0Provider>
    ),
    children: [
      {
        path: "",
        label: "Home",
        isAvailable: true,
        showInNavigation: false,
        element: <HomePageWrapper />,
      },
      {
        path: "step",
        label: "Step",
        isAvailable: true,
        showInNavigation: false,
        element: <HomePageWrapper />,
        children: [
          {
            path: ":stepId",
            label: "Step",
            isAvailable: true,
            showInNavigation: false,
            element: <HomePageWrapper />,
            children: [
              {
                path: ":substepId",
                label: "Substep",
                isAvailable: true,
                showInNavigation: false,
                element: <HomePageWrapper />,
              },
            ],
          },
        ],
      },
      {
        path: "customer/login/callback",
        label: "Consumer Login Callback",
        isAvailable: true,
        showInNavigation: false,
        element: <LoginCallback clientType="consumer" />,
      },
      {
        path: "customer/logout/success",
        label: "Consumer Logout Success",
        isAvailable: true,
        showInNavigation: false,
        element: <LogoutSuccess clientType="consumer" />,
      },
      {
        path: "customer/invite",
        label: "Consumer Invite",
        isAvailable: true,
        showInNavigation: false,
        element: <ConsumerInvitePage />,
      },
      {
        path: "application-submitted",
        label: "Application Submitted",
        isAvailable: true,
        showInNavigation: false,
        element: <ApplicationSubmittedPage />,
      },
      {
        path: "credit-authorization/:creditAuthorizationId",
        label: "Credit Authorization",
        isAvailable: true,
        showInNavigation: false,
        element: (
          <RequireAuth clientType="consumer">
            <CreditAuthorizationPage />
          </RequireAuth>
        ),
      },
      {
        path: "*",
        label: "Not Found",
        isAvailable: true,
        showInNavigation: false,
        element: <NotFound homePath="/" />,
      },
    ],
  },
  {
    path: "/customer/portal",
    label: "Consumer Portal",
    isAvailable: true,
    showInNavigation: false,
    element: (
      <MultiAuth0Provider clientType="consumer">
        <UserContextProvider>
          <SessionContextProvider>
            <RequireAuth clientType="consumer">
              <PagesLayout redirectPath="/customer/portal" />
            </RequireAuth>
          </SessionContextProvider>
        </UserContextProvider>
      </MultiAuth0Provider>
    ),
    children: [
      {
        path: "",
        label: "Consumer Portal",
        isAvailable: true,
        showInNavigation: false,
        element: <ConsumerPortalPage />,
      },
      {
        path: "tasks",
        label: "Consumer Portal Tasks",
        isAvailable: true,
        showInNavigation: false,
        element: <ConsumerPortalTasksPage />,
      },
      {
        path: "applications",
        label: "Consumer Portal Applications",
        isAvailable: true,
        showInNavigation: false,
        element: <ConsumerPortalApplicationsPage />,
      },
      {
        path: "applications/:financialApplicationId",
        label: "Consumer Portal Application View",
        isAvailable: true,
        showInNavigation: false,
        element: <ConsumerPortalApplicationViewPage />,
        children: [
          {
            path: "summary",
            label: "Application Summary",
            containerKey: "summary",
          },
          {
            path: "tasks",
            label: "Tasks",
            containerKey: "tasks",
          },
        ],
      },
      {
        path: "*",
        label: "Not Found",
        isAvailable: true,
        showInNavigation: false,
        element: <NotFound homePath="/customer/portal" />,
      },
    ],
  },
];

// Banker Routes (Protected admin interface)
const bankerRoutes: utils.routes.UclRouteObject[] = [
  {
    path: "/banker",
    label: "Dashboard",
    icon: faDashboard,
    isAvailable: true,
    showInNavigation: true,
    showInSubNavigation: true,
    element: (
      <MultiAuth0Provider clientType="banker">
        <UserContextProvider>
          <SessionContextProvider>
            <RequireAuth clientType="banker">
              <PagesLayout redirectPath="/banker/applications" hideContactButton />
            </RequireAuth>
          </SessionContextProvider>
        </UserContextProvider>
      </MultiAuth0Provider>
    ),
    children: [
      {
        path: "applications",
        label: "Banker Dashboard",
        isAvailable: true,
        showInNavigation: false,
        showInSubNavigation: false,
        element: <BankerApplicationsPage />,
      },
      {
        path: "applications/:financialApplicationId",
        label: "Banker Application View",
        isAvailable: true,
        showInNavigation: false,
        element: <BankerApplicationViewPage />,
        children: [
          {
            path: "details",
            label: "Application Details",
            containerKey: "details",
            children: [
              {
                path: "product-details",
                label: "Product Details",
                containerKey: "product-details",
              },
              {
                path: "client-details",
                label: "Client Details",
                containerKey: "client-details",
              },
              {
                path: "joint-account-owners",
                label: "Joint Account Owners",
                containerKey: "joint-account-owners",
              },
              {
                path: "personal-account-beneficiaries",
                label: "Personal Account Beneficiaries",
                containerKey: "personal-account-beneficiaries",
              },
              {
                path: "organization-info",
                label: "Entity Info",
                containerKey: "organization-info",
              },
              {
                path: "key-people",
                label: "Key People",
                containerKey: "key-people",
              },
              {
                path: "personal-acceptable-use",
                label: "Personal Acceptable Use",
                containerKey: "personal-acceptable-use",
              },
              {
                path: "organization-acceptable-use",
                label: "Entity Acceptable Use",
                containerKey: "organization-acceptable-use",
              },
              {
                path: "opt-ins",
                label: "Account Features & Preferences",
                containerKey: "opt-ins",
              },
              {
                path: "financial-application-documents",
                label: "Application Documents",
                containerKey: "financial-application-documents",
              },
              {
                path: "personal-account-risk-review",
                label: "Personal Account Risk Review",
                containerKey: "personal-account-risk-review",
              },
              {
                path: "law-firm-organization-account-risk-review",
                label: "Law Firm Risk Review",
                containerKey: "law-firm-organization-account-risk-review",
              },
              {
                path: "non-law-firm-organization-account-risk-review",
                label: "Non-Law Firm Risk Review",
                containerKey: "non-law-firm-organization-account-risk-review",
              },
            ],
          },
          {
            path: "tasks",
            label: "Tasks",
            containerKey: "tasks",
          },
          {
            path: "sanction-matches",
            label: "Sanction Matches",
            containerKey: "sanction-matches",
          },
          {
            path: "credit-authorizations",
            label: "Credit Authorizations",
            containerKey: "credit-authorizations",
          },
          {
            path: "underwriting-rules",
            label: "Underwriting Rules",
            containerKey: "underwriting-rules",
          },
        ],
      },
      {
        path: "login/callback",
        label: "Banker Login Callback",
        isAvailable: true,
        showInNavigation: false,
        element: <LoginCallback clientType="banker" />,
      },
      {
        path: "logout/success",
        label: "Banker Logout Success",
        isAvailable: true,
        showInNavigation: false,
        element: <LogoutSuccess clientType="banker" />,
      },
      {
        path: "*",
        label: "Not Found",
        isAvailable: true,
        showInNavigation: false,
        element: <NotFound homePath="/banker" />,
      },
    ],
  },
  {
    path: "/banker/admin",
    label: "Admin",
    icon: faGear,
    isAvailable: true,
    showInNavigation: true,
    showInSubNavigation: true,
    element: (
      <MultiAuth0Provider clientType="banker">
        <UserContextProvider>
          <SessionContextProvider>
            <RequireAuth clientType="banker">
              <PagesLayout redirectPath="/banker" hideContactButton />
            </RequireAuth>
          </SessionContextProvider>
        </UserContextProvider>
      </MultiAuth0Provider>
    ),
    children: [
      {
        path: "",
        label: "Banker Admin",
        isAvailable: true,
        showInNavigation: false,
        showInSubNavigation: false,
        element: <BankerAdminPage />,
        children: [
          {
            path: "document-templates",
            label: "Document Templates",
            containerKey: "document-templates",
          },
          {
            path: "task-templates",
            label: "Task Templates",
            containerKey: "task-templates",
          },
          {
            path: "task-rules",
            label: "Task Rules",
            containerKey: "task-rules",
          },
        ],
      },
      {
        path: "*",
        label: "Not Found",
        isAvailable: true,
        showInNavigation: false,
        element: <NotFound homePath="/banker/admin" />,
      },
    ],
  },
];

export const routeObject = [...consumerRoutes, ...bankerRoutes];

export const browserRouter = createBrowserRouter(routeObject, {
  basename: import.meta.env.VITE_PATH_PREFIX,
});
